import {
  useMutation,
  useQueryClient,
  UseMutationResult,
  UseMutationOptions,
} from "@tanstack/react-query"
import { guardAppUsage } from "@mimber-client/guards/AppGuard"
import { useServerContext } from "@mimber-client/contexts/ServerContext"
import {
  OrganizationProfileSettingsDto,
  UpdateOrganizationProfileDto,
  ResponseDto,
} from "@mimber/schema"
import { organizationKeys } from "../organization.utils"

interface UpdateOrganizationProfileOptions extends Omit<
  UseMutationOptions<
    ResponseDto<OrganizationProfileSettingsDto>,
    Error,
    UpdateOrganizationProfileDto
  >,
  "mutationFn"
> {}

/**
 * Hook for updating organization profile data
 *
 * Updates branding, contact information, social media links, and public profile settings.
 *
 * @param uuid - Organization UUID
 * @param options - Additional options for the mutation
 * @returns Mutation hook for updating profile
 *
 * @example
 * ```tsx
 * const updateProfile = useUpdateOrganizationProfile(organizationUuid)
 *
 * updateProfile.mutate({
 *   aboutText: "We are a tech company",
 *   primaryColor: "#FF5733",
 *   socialMediaTwitter: "https://twitter.com/acmecorp"
 * })
 * ```
 */
export const useUpdateOrganizationProfile = (
  uuid: string,
  options: UpdateOrganizationProfileOptions = {}
): UseMutationResult<
  ResponseDto<OrganizationProfileSettingsDto>,
  Error,
  UpdateOrganizationProfileDto
> => {
  guardAppUsage("mimber-organization", "useUpdateOrganizationProfile")

  const { server } = useServerContext()
  const queryClient = useQueryClient()

  return useMutation({
    ...options,
    mutationFn: async (data: UpdateOrganizationProfileDto) => {
      const response = await server.patch<
        ResponseDto<OrganizationProfileSettingsDto>
      >(`organizations/${uuid}/profile`, data)
      return response.data
    },
    onSuccess: (response, variables, onMutateResult, context) => {
      // Invalidate current organization query to refetch updated data
      queryClient.invalidateQueries({
        queryKey: organizationKeys.getCurrentOrganization(),
      })

      if (options?.onSuccess) {
        options.onSuccess(response, variables, onMutateResult, context)
      }
    },
  })
}
